import React, { useState } from "react";
import { ThumbsUp, ThumbsDown, MessageSquare, Eye, Camera, ArrowRight } from "lucide-react";
import { TrustBadges } from "./TrustBadges";
import { MemberOperationsToolbar } from "./MemberOperationsToolbar";
import { FullAssessmentDialog } from "./FullAssessmentDialog";
import { LikeButton } from "./LikeButton";
import WatchButton from "../../command/WatchButton";
import { PaperPatina } from "../realism/PaperPatina";
import { PaperMarginalia, useDossierProvenance } from "./PaperMarginalia";

const _TEX = process.env.PUBLIC_URL ? `${process.env.PUBLIC_URL}/textures` : "/textures";

// Deterministic paper-variant picker (1..6) so no two dossiers use the
// same aged-paper texture. Matches Spotlight._paperVar for consistency.
const _paperVar = (seed) => {
  let h = 0; const s = String(seed || "");
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  return (Math.abs(h) % 6) + 1;
};
// Procedural material layers (edge-to-edge by construction, so the
// texture always conforms to the full folder silhouette):
//   1. fine fibre speckle (tiled SVG turbulence, multiply)
//   2. directional paper-fibre streaks (anisotropic turbulence)
//   3-4. deterministic aging mottle (very low-alpha radial washes)
//   5. one faint pressure crease (soft-light: shadow + catch-light)
//   6. photographed paper PNG (150% over-scale, see PaperPatina.jsx)
const _GRAIN =
  `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='240' height='240'%3E%3Cfilter id='g'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.55' numOctaves='4' stitchTiles='stitch' seed='7'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3CfeComponentTransfer%3E%3CfeFuncA type='linear' slope='0.10' intercept='0'/%3E%3C/feComponentTransfer%3E%3C/filter%3E%3Crect width='240' height='240' filter='url(%23g)'/%3E%3C/svg%3E")`;
const _FIBRE =
  `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='420' height='420'%3E%3Cfilter id='f'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.012 0.28' numOctaves='3' stitchTiles='stitch' seed='13'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3CfeComponentTransfer%3E%3CfeFuncA type='linear' slope='0.05' intercept='0'/%3E%3C/feComponentTransfer%3E%3C/filter%3E%3Crect width='420' height='420' filter='url(%23f)'/%3E%3C/svg%3E")`;

const _paperBg = (seed) => {
  const v = _paperVar(seed);
  const mx = 12 + ((v * 37) % 60);
  const my = 10 + ((v * 53) % 55);
  const cx = 22 + ((v * 29) % 52);
  return {
    backgroundImage: [
      _GRAIN,
      _FIBRE,
      `radial-gradient(ellipse 55% 42% at ${mx}% ${my}%, rgba(96,70,34,0.05), transparent 70%)`,
      `radial-gradient(ellipse 48% 38% at ${100 - mx}% ${100 - my}%, rgba(96,70,34,0.05), transparent 72%)`,
      `linear-gradient(90deg, transparent ${cx - 0.7}%, rgba(58,38,16,0.05) ${cx}%, rgba(255,246,220,0.06) ${cx + 0.35}%, transparent ${cx + 1.1}%)`,
      `url("${_TEX}/paper-0${v}.png")`,
    ].join(", "),
    // 150% over-scale on the PNG — see PaperPatina.jsx / Spotlight.jsx
    // (crops out the wooden backdrop baked into each paper PNG so the
    // dossier reads as a single sheet on the desk).
    backgroundSize: "240px 240px, 420px 420px, auto, auto, auto, 150% 150%",
    backgroundPosition: "0 0, 0 0, center, center, center, center",
    backgroundRepeat: "repeat, repeat, no-repeat, no-repeat, no-repeat, no-repeat",
    backgroundBlendMode: "multiply, multiply, multiply, multiply, soft-light, normal",
  };
};

export const TopicHeader = ({ topic, commentsCount, onVote, myVote = null, voteBusy = false }) => {
  const prov = useDossierProvenance(topic.slug, topic.title);
  const [showAssessment, setShowAssessment] = useState(false);
  return (
  <>
  <div
    className="paper-folder mt-10 p-6 pb-5 sm:p-8 md:p-12 pl-14 sm:pl-16 md:pl-20 md:pr-40 relative"
    style={_paperBg(topic.slug)}
    data-testid="topic-folder"
  >
    <PaperPatina seed={`topic-header-${topic.slug}`} variant="folder" intensity={1.0} />
    <PaperMarginalia slug={topic.slug} title={topic.title} category={topic.category} />
    <div className="flex flex-wrap items-center gap-3 mb-6">
      <span className="stamp" style={{ color: "#B02418", borderColor: "#B02418" }}>CASE FILE</span>
      <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#4a3a1e]">
        CAT: {topic.category}
      </span>
      <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#6b563a]">
        REF: {topic.slug}
      </span>
    </div>
    <h1
      className="font-display text-4xl md:text-6xl leading-[0.95] tracking-tight text-[#1a1208] mb-4"
      data-testid="topic-title"
    >
      {topic.title}
    </h1>
    <p className="text-xl text-[#3a2c18] font-heading italic mb-3 sm:mb-4 max-w-3xl">{topic.tagline}</p>

    {/* Trust badges */}
    <TrustBadges topic={topic} dense={true} />

    {/* -------- Archival provenance row --------
        A real archive file always carries its declassification
        stamp + the archive-box index. Both are deterministic per
        dossier slug so the same case file always reads identical. */}
    <div className="mt-4 sm:mt-6 flex flex-wrap items-end gap-3 sm:gap-4" data-testid="dossier-provenance">
      <span className="paper-declassified-stamp" aria-label="Declassified">
        <span className="paper-declassified-stamp__title">DECLASSIFIED</span>
        <span className="paper-declassified-stamp__body">NATIONAL ARCHIVES</span>
        <span className="paper-declassified-stamp__date">DATE: {prov.declassifyDate}</span>
      </span>
      <span className="paper-archive-box-card" aria-label="Archive box">
        <span className="paper-archive-box-card__label">ARCHIVE BOX</span>
        <span className="paper-archive-box-card__id">{prov.catalog}</span>
        <span className="paper-archive-box-card__shelf">{prov.shelf}</span>
      </span>
    </div>

    {/* Compact evidence assessment — the physical cover carries only
        the case identity + evidence summary. The full 4-metric panel
        (Controversy / Evidence / Source Quality / Consensus, with
        explanations) lives in the Full Assessment dialog. */}
    <div className="border-t border-[#8c7345]/40 pt-4 mt-5 sm:pt-5 sm:mt-8" data-testid="evidence-assessment">
      <div className="font-mono text-[9px] font-bold uppercase tracking-[0.3em] text-[#B02418] mb-2.5 sm:mb-3">
        // EVIDENCE ASSESSMENT
      </div>
      <div className="space-y-1.5 font-mono text-xs sm:text-sm font-medium text-[#241a0d] tracking-[0.08em]">
        <div data-testid="assessment-evidence">
          EVIDENCE STRENGTH — <span className="font-bold text-[#1d3560]">{Math.max(0, Math.min(100, Number(topic.evidence_score ?? 60)))}/100</span>
        </div>
        <div data-testid="assessment-quality">
          SOURCE QUALITY — <span className="font-bold text-[#1f4423]">{Math.max(0, Math.min(100, Number(topic.source_quality_score ?? 60)))}/100</span>
        </div>
      </div>
      <button
        type="button"
        onClick={() => setShowAssessment(true)}
        data-testid="view-full-assessment-btn"
        className="mt-2.5 sm:mt-3 inline-flex items-center gap-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-[#B02418] hover:text-[#8E2A26] underline underline-offset-4 decoration-[#B02418]/70 transition-colors"
      >
        View Full Assessment <ArrowRight size={11} />
      </button>
    </div>
  </div>

  {/* ---- Digital member controls — black Archive interface, off the
          physical folder. Physical folder = case identity + evidence
          summary; interface = visitor/member actions. ---- */}
  <div className="mt-6" data-testid="dossier-ops-panel">
    <MemberOperationsToolbar topic={topic} />

    {/* Engagement — one compact row on mobile (like · votes · views ·
        comments · watch), spaced strip on sm+ */}
    <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-[#2D2A26] flex flex-wrap items-center gap-x-4 sm:gap-x-8 gap-y-1.5">
      <div className="flex items-center gap-3 sm:gap-3">
        <span className="hidden sm:inline font-mono text-[9px] uppercase tracking-[0.3em] text-[#757065]">
          // VOTES
        </span>
        <LikeButton slug={topic.slug} />
        <button
          type="button"
          onClick={() => onVote("up")}
          disabled={voteBusy}
          aria-pressed={myVote === "up"}
          data-testid="upvote-btn"
          className={
            "flex items-center gap-1.5 font-mono text-sm min-h-[44px] sm:min-h-0 px-1 sm:px-0 transition-colors " +
            "disabled:opacity-50 disabled:cursor-not-allowed " +
            (myVote === "up"
              ? "text-[#D92D20]"
              : "text-[#A39E93] hover:text-[#D92D20]")
          }
        >
          <ThumbsUp size={14} className={myVote === "up" ? "fill-[#D92D20]" : ""} /> {topic.upvotes}
        </button>
        <button
          type="button"
          onClick={() => onVote("down")}
          disabled={voteBusy}
          aria-pressed={myVote === "down"}
          data-testid="downvote-btn"
          className={
            "flex items-center gap-1.5 font-mono text-sm min-h-[44px] sm:min-h-0 px-1 sm:px-0 transition-colors " +
            "disabled:opacity-50 disabled:cursor-not-allowed " +
            (myVote === "down"
              ? "text-[#E4DFD3]"
              : "text-[#A39E93] hover:text-[#E4DFD3]")
          }
        >
          <ThumbsDown size={14} className={myVote === "down" ? "fill-[#E4DFD3]" : ""} /> {topic.downvotes}
        </button>
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 font-mono text-xs text-[#757065]">
        <span className="flex items-center gap-1"><Eye size={12} /> {topic.views}</span>
        <span className="flex items-center gap-1"><MessageSquare size={12} /> {commentsCount}</span>
        {(topic.authenticated_image_count ?? 0) > 0 && (
          <span className="flex items-center gap-1.5 uppercase tracking-[0.2em] text-[10px]"
                data-testid="authenticated-images-count">
            <Camera size={12} aria-hidden="true" />
            <span className="hidden sm:inline">Authenticated Images:</span> {topic.authenticated_image_count}
          </span>
        )}
        <WatchButton slug={topic.slug} />
      </div>
    </div>
  </div>

  <FullAssessmentDialog open={showAssessment} onOpenChange={setShowAssessment} topic={topic} />
  </>
  );
};

export default TopicHeader;
